import { useParams } from "react-router";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteWorkOrderDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/delete-work-order-dialog";
import { WorkOrderPdf } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/work-order-pdf";
import { useGetWorkOrder } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/_components/use-work-order-offline";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import { WorkOrderProviders } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-providers";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/htz/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { FulfillmentStateBadge } from "@/services/backend/relationships/components/fulfillment-state-badge";
import { Separator } from "@/shared/components/ui/separator";
import { CancelWorkOrderDialog } from "@/services/backend/htz/work-order/components/cancel-work-order-dialog";
import { StartExecutionDialog } from "@/services/backend/htz/work-order/components/start-execution-dialog";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { useState } from "react";
import { v4 } from "uuid";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/htz/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderPlanningRoute() {
  const { workOrderId } = useParams();

  return (
    <WorkOrderProviders workOrderId={workOrderId!}>
      <WorkOrderPlanningPage workOrderId={workOrderId!} />
    </WorkOrderProviders>
  );
}

function WorkOrderPlanningPage({ workOrderId }: { workOrderId: string }) {
  const { data: workOrder, isLoading, error } = useGetWorkOrder(workOrderId);

  useWorkOrderStateGuard(
    workOrder,
    FulfillmentState.Planning,
    FulfillmentState.Zero,
  );

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="planning">
      {t("Planung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error ? <RTKQueryErrorAlert error={error} /> : null}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && <Page workOrder={workOrder} />}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  const { notReadyError } = useErrDocumentNotReady();

  const hasDataError = hasFieldError(
    notReadyError,
    "state",
    "executingBranchId",
    "beginDate",
    "endDate",
    "teamLeader",
    "address",
    "representative",
  );
  const hasPosError = hasFieldError(notReadyError, "position");

  const links = [
    { to: "data", hasValidationError: hasDataError, label: t("Arbeitsschein") },
    {
      to: "positions",
      hasValidationError: hasPosError,
      label: t("Leistungen"),
    },
  ];

  const [key, setKey] = useState(v4());

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              hasValidationError={link.hasValidationError}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <FulfillmentStateBadge state={workOrder.state} />
        <DocumentPageActions>
          <PersistenceStateUsingContext className="mr-2" />
          <Separator orientation="vertical" className="min-h-8" />
          <WorkOrderPdf workOrder={workOrder} />
          <CpyDialog
            destinationEntityName={CpyName.HTZWorkOrder}
            destinationId={workOrder.id}
            onCpySuccess={() => {
              // Await refetch from server
              setTimeout(() => {
                setKey(v4());
              }, 1000);
            }}
          />
          <Separator orientation="vertical" className="min-h-8" />
          <DeleteWorkOrderDialog workOrderId={workOrder.id} />
          <CancelWorkOrderDialog workOrder={workOrder} />
          <StartExecutionDialog workOrder={workOrder} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent key={key}>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
